<template>
	<div class="xd-phone-input" :class="{ 'is-focus': isFocused, 'is-error': hasError, 'is-disabled': disabled }">
		<!-- 区号选择器 -->
		<el-popover
			ref="popover"
			placement="bottom-start"
			width="320"
			trigger="click"
			:disabled="disabled"
			popper-class="xd-phone-popover"
			@show="onPopoverShow">
			<div class="xpi-popover">
				<div class="xpi-search">
					<i class="el-icon-search"></i>
					<input v-model="searchKeyword" ref="searchInput" type="text"
						class="xpi-search-input" placeholder="搜索国家/地区/区号">
					<i v-if="searchKeyword" class="el-icon-circle-close xpi-search-clear"
						@click="searchKeyword = ''"></i>
				</div>

				<!-- 自定义区号入口 -->
				<div v-if="customCodeFromKeyword" class="xpi-custom"
					@click="useCustomCode">
					<div class="xpi-custom-icon"><i class="el-icon-plus"></i></div>
					<div class="xpi-custom-info">
						<div class="xpi-custom-title">使用自定义区号「{{ customCodeFromKeyword }}」</div>
						<div class="xpi-custom-sub">列表中未匹配？直接使用此区号</div>
					</div>
					<i class="el-icon-check"></i>
				</div>

				<div class="xpi-list">
					<div v-for="(country, idx) in filteredCountries"
						:key="country.iso2 || ('cu-' + idx)"
						class="xpi-list-item"
						:class="{ selected: isSelected(country) }"
						@click="selectCountry(country)">
						<img v-if="country.icon" :src="country.icon" class="xpi-flag"/>
						<div v-else class="xpi-flag xpi-flag-fallback"><i class="el-icon-location-information"></i></div>
						<span class="xpi-name">{{ country.name }}</span>
						<span class="xpi-code">{{ country.code }}</span>
						<i v-if="isSelected(country)" class="el-icon-check xpi-check"></i>
					</div>
					<div v-if="filteredCountries.length === 0 && !customCodeFromKeyword" class="xpi-empty">
						未找到匹配，输入「+数字」可使用自定义区号
					</div>
				</div>
			</div>

			<div slot="reference" class="xpi-selector">
				<img v-if="selectedCountry.icon" :src="selectedCountry.icon" class="xpi-flag"/>
				<div v-else class="xpi-flag xpi-flag-fallback"><i class="el-icon-location-information"></i></div>
				<span class="xpi-selector-code">{{ selectedCountry.code || '+86' }}</span>
				<i class="el-icon-arrow-down xpi-arrow"></i>
			</div>
		</el-popover>

		<div class="xpi-divider"></div>

		<input
			v-model="phoneNumber"
			type="text"
			class="xpi-input"
			:placeholder="selectedCountry.placeholder || '请输入手机号码'"
			:maxlength="(selectedCountry.maxLength || 15) + 4"
			:disabled="disabled"
			@input="onPhoneInput"
			@focus="isFocused = true"
			@blur="isFocused = false; validatePhone()">
		<i v-if="phoneNumber && !disabled" class="el-icon-circle-close xpi-clear"
			@click="clearPhone"></i>
	</div>
</template>

<script>
import { countries } from './countries.js';

export default {
	name: 'XdPhoneInput',
	props: {
		value: { type: String, default: '' },           // 完整号码 "+861380013800"
		defaultCountry: { type: String, default: 'CN' },
		disabled: { type: Boolean, default: false },
	},
	data() {
		return {
			countries,
			searchKeyword: '',
			selectedCountry: {},
			phoneNumber: '',
			isFocused: false,
			hasError: false,
			hasInitialValue: false,
		};
	},
	computed: {
		filteredCountries() {
			const k = (this.searchKeyword || '').trim().toLowerCase();
			if (!k) return this.countries;
			return this.countries.filter(c =>
				(c.name || '').toLowerCase().includes(k) ||
				(c.iso2 || '').toLowerCase().includes(k) ||
				(c.code || '').includes(k));
		},
		customCodeFromKeyword() {
			const raw = (this.searchKeyword || '').trim();
			if (!raw) return '';
			const digits = raw.replace(/[^0-9]/g, '');
			if (!digits || digits.length < 1 || digits.length > 4) return '';
			const code = '+' + digits;
			if (this.countries.some(c => c.code === code)) return '';
			return code;
		},
		fullNumber() {
			if (!this.phoneNumber) return '';
			return (this.selectedCountry.code || '') + this.phoneNumber;
		},
	},
	watch: {
		value: {
			immediate: true,
			handler(val) {
				this.parsePhoneNumber(val);
				if (val && !this.hasInitialValue) this.hasInitialValue = true;
			},
		},
	},
	mounted() {
		this.initializeCountry();
	},
	methods: {
		initializeCountry() {
			if (this.hasInitialValue && this.selectedCountry && this.selectedCountry.iso2) return;
			this.selectedCountry = this.countries.find(c => c.iso2 === this.defaultCountry) || this.countries[0];
		},
		parsePhoneNumber(fullNumber) {
			if (!fullNumber) { this.phoneNumber = ''; return; }
			for (const c of this.countries) {
				if (fullNumber.startsWith(c.code)) {
					this.selectedCountry = c;
					this.phoneNumber = fullNumber.substring(c.code.length).replace(/^[\s\-()]+/, '');
					return;
				}
			}
			// 自定义区号 fallback
			const m = String(fullNumber).match(/^\+(\d{1,4})(.*)$/);
			if (m) {
				this.selectedCountry = {
					name: '自定义', iso2: 'CUSTOM', code: '+' + m[1],
					icon: '', placeholder: '请输入号码', maxLength: 15,
				};
				this.phoneNumber = (m[2] || '').replace(/^[\s\-()]+/, '');
			}
		},
		isSelected(c) {
			return c.iso2 === this.selectedCountry.iso2 && c.code === this.selectedCountry.code;
		},
		selectCountry(c) {
			this.selectedCountry = c;
			this.$refs.popover && this.$refs.popover.doClose();
			this.searchKeyword = '';
			this.validatePhone();
			this.emitChange();
		},
		useCustomCode() {
			const code = this.customCodeFromKeyword;
			if (!code) return;
			this.selectCountry({
				name: '自定义', iso2: 'CUSTOM', code,
				icon: '', placeholder: '请输入号码', maxLength: 15,
			});
		},
		onPopoverShow() {
			this.$nextTick(() => {
				if (this.$refs.searchInput) this.$refs.searchInput.focus();
			});
		},
		onPhoneInput() {
			// 用户可能粘贴带区号的号码，自动剥离
			if (this.phoneNumber.startsWith('+')) {
				const tryParse = this.phoneNumber;
				for (const c of this.countries) {
					if (tryParse.startsWith(c.code)) {
						this.selectedCountry = c;
						this.phoneNumber = tryParse.substring(c.code.length).replace(/^[\s\-()]+/, '');
						break;
					}
				}
			}
			// 只保留数字 + 空格连字符
			this.phoneNumber = this.phoneNumber.replace(/[^\d\s\-()]/g, '');
			this.validatePhone();
			this.emitChange();
		},
		clearPhone() {
			this.phoneNumber = '';
			this.validatePhone();
			this.emitChange();
		},
		validatePhone() {
			this.hasError = false;
			if (!this.phoneNumber) return;
			const cleaned = this.phoneNumber.replace(/[\s\-()]/g, '');
			const pattern = this.selectedCountry.pattern;
			if (pattern && !pattern.test(cleaned)) {
				this.hasError = true;
			}
		},
		emitChange() {
			this.$emit('input', this.fullNumber);
			this.$emit('change', {
				fullNumber: this.fullNumber,
				countryCode: this.selectedCountry.code || '',
				countryIso2: this.selectedCountry.iso2 || '',
				phoneNumber: this.phoneNumber.replace(/[\s\-()]/g, ''),
				country: this.selectedCountry,
				isValid: !this.hasError,
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.xd-phone-input {
	display: flex;
	align-items: center;
	background: #f8f8f8;
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	height: 36px;
	padding: 0 6px 0 0;
	transition: all .2s;
	position: relative;

	&.is-focus {
		background: #fff;
		border-color: #ff6b35;
		box-shadow: 0 0 0 2px rgba(255, 107, 53, .12);
	}
	&.is-error {
		border-color: #f56c6c;
		box-shadow: 0 0 0 2px rgba(245, 108, 108, .12);
	}
	&.is-disabled {
		background: #f5f7fa;
		cursor: not-allowed;
	}
}

.xpi-selector {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 0 10px;
	height: 100%;
	cursor: pointer;
	user-select: none;
	border-radius: 6px 0 0 6px;
	&:hover { background: rgba(255, 107, 53, .06); }
}
.xpi-flag {
	width: 22px;
	height: 16px;
	border-radius: 2px;
	object-fit: cover;
	flex-shrink: 0;
}
.xpi-flag-fallback {
	display: flex; align-items: center; justify-content: center;
	background: rgba(255, 107, 53, .1);
	color: #ff6b35;
	border: 1px dashed rgba(255, 107, 53, .3);
	font-size: 11px;
}
.xpi-selector-code {
	font-size: 13px;
	font-weight: 500;
	color: #303133;
}
.xpi-arrow {
	color: #909399;
	font-size: 12px;
	transition: transform .2s;
}

.xpi-divider {
	width: 1px;
	height: 18px;
	background: #e0e0e0;
	margin: 0 4px;
}

.xpi-input {
	flex: 1;
	height: 100%;
	border: none;
	outline: none;
	background: transparent;
	font-size: 13px;
	color: #303133;
	padding: 0 6px;
	min-width: 0;
	&::placeholder { color: #bbb; }
	&:disabled { cursor: not-allowed; color: #909399; }
}

.xpi-clear {
	color: #c0c4cc;
	font-size: 16px;
	cursor: pointer;
	padding: 4px;
	&:hover { color: #909399; }
}
</style>

<style lang="scss">
/* popover 内部不能用 scoped，否则深层选择器失效 */
.xd-phone-popover.el-popover {
	padding: 0 !important;
	border-radius: 10px !important;
	overflow: hidden;
	border: 1px solid #ebeef5;
	box-shadow: 0 8px 24px rgba(0, 0, 0, .12);

	.xpi-popover { width: 100%; }

	.xpi-search {
		display: flex; align-items: center;
		gap: 8px;
		padding: 10px 12px;
		border-bottom: 1px solid #ebeef5;
		background: #fafafa;
		.el-icon-search { color: #909399; font-size: 14px; }
		.xpi-search-input {
			flex: 1;
			border: none; outline: none;
			background: transparent;
			font-size: 13px;
			color: #303133;
			&::placeholder { color: #c0c4cc; }
		}
		.xpi-search-clear {
			color: #c0c4cc; font-size: 14px; cursor: pointer;
			&:hover { color: #909399; }
		}
	}

	.xpi-custom {
		display: flex; align-items: center; gap: 10px;
		padding: 10px 12px;
		margin: 8px 8px 0;
		background: linear-gradient(135deg, rgba(255, 107, 53, .10), rgba(247, 123, 59, .06));
		border: 1px dashed rgba(255, 107, 53, .4);
		border-radius: 8px;
		cursor: pointer;
		&:hover { background: linear-gradient(135deg, rgba(255, 107, 53, .15), rgba(247, 123, 59, .10)); }
		.xpi-custom-icon {
			width: 28px; height: 28px; border-radius: 50%;
			background: linear-gradient(135deg, #ff6b35, #f77b3b);
			color: #fff; display: flex; align-items: center; justify-content: center;
			flex-shrink: 0;
		}
		.xpi-custom-info { flex: 1; min-width: 0; }
		.xpi-custom-title { font-size: 13px; font-weight: 600; color: #ff6b35; }
		.xpi-custom-sub { font-size: 11px; color: #909399; margin-top: 2px; }
		.el-icon-check { color: #ff6b35; font-size: 16px; }
	}

	.xpi-list {
		max-height: 280px;
		overflow-y: auto;
		padding: 6px 0;
	}
	.xpi-list-item {
		display: flex; align-items: center;
		gap: 10px;
		padding: 8px 14px;
		cursor: pointer;
		transition: background .15s;
		&:hover { background: rgba(255, 107, 53, .06); }
		&.selected { background: rgba(255, 107, 53, .10); }
		.xpi-flag {
			width: 22px; height: 16px; border-radius: 2px; object-fit: cover; flex-shrink: 0;
		}
		.xpi-flag-fallback {
			display: flex; align-items: center; justify-content: center;
			background: rgba(255, 107, 53, .1); color: #ff6b35;
			border: 1px dashed rgba(255, 107, 53, .3); font-size: 10px;
		}
		.xpi-name {
			flex: 1; font-size: 13px; color: #303133;
			overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
		}
		.xpi-code {
			font-size: 12px; color: #909399; font-family: Consolas, monospace;
		}
		.xpi-check { color: #ff6b35; font-size: 16px; }
	}
	.xpi-empty {
		padding: 40px 20px; text-align: center;
		color: #909399; font-size: 12px;
	}
}
</style>
